import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

const SITE_URL = "https://paranoidi.fi";

const DEFAULT_IMAGE_FI = "/paranoidi-og-fi.jpg";
const DEFAULT_IMAGE_EN = "/paranoidi-og-en.jpg";

const SEO_LANGUAGE_ROUTES = {
  "/": {
    fi: "/",
    en: "/en/",
  },

  "/en": {
    fi: "/",
    en: "/en/",
  },

  "/ominaisuudet": {
    fi: "/ominaisuudet",
    en: "/en/features",
  },

  "/en/features": {
    fi: "/ominaisuudet",
    en: "/en/features",
  },

  "/biisitoiveet-keikalle": {
    fi: "/biisitoiveet-keikalle",
    en: "/en/song-requests",
  },

  "/en/song-requests": {
    fi: "/biisitoiveet-keikalle",
    en: "/en/song-requests",
  },

  "/tietosuoja": {
    fi: "/tietosuoja",
    en: "/en/privacy",
  },

  "/en/privacy": {
    fi: "/tietosuoja",
    en: "/en/privacy",
  },

  "/kayttoehdot": {
    fi: "/kayttoehdot",
    en: "/en/terms",
  },

  "/en/terms": {
    fi: "/kayttoehdot",
    en: "/en/terms",
  },
};

function normalizePath(path) {
  if (!path || path === "/") {
    return "/";
  }

  const withLeadingSlash = path.startsWith("/") ? path : `/${path}`;

  return withLeadingSlash.replace(/\/+$/, "") || "/";
}

function buildAbsoluteUrl(path) {
  if (!path || path === "/") {
    return `${SITE_URL}/`;
  }

  return `${SITE_URL}${path}`;
}

export default function Seo({
  title,
  description,
  path = "/",
  image = null,
  noindex = false,
}) {
  const { i18n } = useTranslation();

  const normalizedPath = normalizePath(path);

  const isEnglishSeoPath =
    normalizedPath === "/en" || normalizedPath.startsWith("/en/");

  const i18nIsEnglish = String(
    i18n.resolvedLanguage || i18n.language || "fi"
  )
    .toLowerCase()
    .startsWith("en");

  /*
   * Julkisilla SEO-sivuilla URL määrää kielen.
   *
   * Muilla sivuilla, kuten /login, /admin ja /p/:slug,
   * käytetään normaalisti i18nextin aktiivista käyttöliittymäkieltä.
   */
  const isEnglish = isEnglishSeoPath || i18nIsEnglish;

  /*
   * Jos sivulle ei ole annettu erillistä image-propia,
   * käytetään kielikohtaista Paranoidin oletus-OG-kuvaa.
   */
  const resolvedImage =
    image || (isEnglish ? DEFAULT_IMAGE_EN : DEFAULT_IMAGE_FI);

  const defaultTitle = isEnglish
    ? "Paranoidi – Song Requests, Setlists & Gig Tools for Performers"
    : "Paranoidi – biisitoiveet, settilistat ja keikkatyökalut";

  const defaultDescription = isEnglish
    ? "Song requests, song library, setlists, reader, gig notes and reports in one browser-based tool for live performers."
    : "Biisitoiveet, biisikirjasto, settilistat, lukutila, keikkamuisti ja raportit yhdessä työkalussa live-esiintyjille.";

  const resolvedTitle = title ?? defaultTitle;
  const resolvedDescription = description ?? defaultDescription;

  const canonicalPath = path === "/" ? "/" : path;
  const canonicalUrl = buildAbsoluteUrl(canonicalPath);

  const imageUrl = resolvedImage.startsWith("http")
    ? resolvedImage
    : `${SITE_URL}${resolvedImage}`;

  const languageAlternates =
    SEO_LANGUAGE_ROUTES[normalizedPath] || null;

  const fiUrl = languageAlternates
    ? buildAbsoluteUrl(languageAlternates.fi)
    : null;

  const enUrl = languageAlternates
    ? buildAbsoluteUrl(languageAlternates.en)
    : null;

  const ogLocale = isEnglish ? "en_US" : "fi_FI";
  const ogAlternateLocale = isEnglish ? "fi_FI" : "en_US";

  return (
    <Helmet
      htmlAttributes={{
        lang: isEnglish ? "en" : "fi",
        dir: "ltr",
      }}
    >
      <title>{resolvedTitle}</title>

      <meta
        name="description"
        content={resolvedDescription}
      />

      <link
        rel="canonical"
        href={canonicalUrl}
      />

      {languageAlternates && (
        <link
          rel="alternate"
          hrefLang="fi"
          href={fiUrl}
        />
      )}

      {languageAlternates && (
        <link
          rel="alternate"
          hrefLang="en"
          href={enUrl}
        />
      )}

      <meta
        name="robots"
        content={noindex ? "noindex,nofollow" : "index,follow"}
      />

      <meta
        property="og:site_name"
        content="Paranoidi"
      />

      <meta
        property="og:type"
        content="website"
      />

      <meta
        property="og:title"
        content={resolvedTitle}
      />

      <meta
        property="og:description"
        content={resolvedDescription}
      />

      <meta
        property="og:url"
        content={canonicalUrl}
      />

      <meta
        property="og:image"
        content={imageUrl}
      />

      <meta
        property="og:locale"
        content={ogLocale}
      />

      <meta
        property="og:locale:alternate"
        content={ogAlternateLocale}
      />

      <meta
        name="twitter:card"
        content="summary_large_image"
      />

      <meta
        name="twitter:title"
        content={resolvedTitle}
      />

      <meta
        name="twitter:description"
        content={resolvedDescription}
      />

      <meta
        name="twitter:image"
        content={imageUrl}
      />
    </Helmet>
  );
}

